import React from 'react';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import XIcon from '../../svgs/x.svg';
import { InfoTooltip } from '../common/InfoTooltip';
import { CourseFilterCheckbox } from './CourseFilterCheckbox';
import { CourseFilterCheckboxGroup } from './CourseFilterCheckboxGroup';
import { ProductCategory, SearchProductTileModel } from '../../models/globals';
import { SearchParamNames, SearchParamValues } from '../../models/enums';

interface FilterCoursesProps {
    courseCountMap: Record<SearchProductTileModel['categoryCode'], number>; // same as Record<ProductCategory['seoName'], number> or Record<string, number>
    courseTypes: Set<string>;
    handleFilterChange: React.FormEventHandler<HTMLInputElement>;
    onHide?(): void;
    productCategories: ProductCategory[];
    subTopics: Set<string>;
    topics: Set<string>;
}

const FilterCourses: React.FC<FilterCoursesProps> = ({
    courseCountMap,
    courseTypes,
    handleFilterChange,
    onHide,
    productCategories,
    subTopics,
    topics,
}) => {
    const isModal = !!onHide;
    const idPrefix = isModal ? 'modal-' : '';
    const placement = isModal ? 'left' : 'right';

    return (
        <>
            <Row className="align-items-center">
                <Col className="text-left">
                    <h3 className="font-weight-light m-0 text-secondary">Filter By:</h3>
                </Col>
                {isModal && (
                    <Col className="text-right" xs="auto">
                        <Button aria-label="Close" className="border-0 m-0 p-0" onClick={onHide} variant="link">
                            <XIcon className="size-3 text-secondary" />
                        </Button>
                    </Col>
                )}
            </Row>

            <fieldset>
                <legend className="mb-0 py-3">
                    <p className="filter-courses-subtitle font-weight-semi-bold my-0 text-black">COURSE TYPES</p>
                </legend>
                <Row className="mb-2">
                    <Col className="pr-0 text-left">
                        <CourseFilterCheckbox
                            checkboxName={SearchParamNames.CourseTypes}
                            checkboxValue={SearchParamValues.Fundamentals}
                            checked={courseTypes.has(SearchParamValues.Fundamentals)}
                            handleFilterChange={handleFilterChange}
                            idPrefix={idPrefix}
                            label="Fundamentals"
                        />
                    </Col>
                    <Col className="text-right" xs="auto">
                        <InfoTooltip
                            id="fundamentals-tooltip"
                            label="Explain Fundamentals"
                            placement={placement}
                            tooltip="Courses that teach basic skills for professional development or personal enrichment."
                        />
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col className="pr-0 text-left">
                        <CourseFilterCheckbox
                            checkboxName={SearchParamNames.CourseTypes}
                            checkboxValue={SearchParamValues.CareerTraining}
                            checked={courseTypes.has(SearchParamValues.CareerTraining)}
                            handleFilterChange={handleFilterChange}
                            idPrefix={idPrefix}
                            label="Advanced Career Training"
                        />
                    </Col>
                    <Col className="text-right" xs="auto">
                        <InfoTooltip
                            id="advanced-career-training-tooltip"
                            label="Explain Advanced Career Training"
                            placement={placement}
                            tooltip="Courses that prepare for a new career, certification, or advancement within a current occupation."
                        />
                    </Col>
                </Row>
            </fieldset>

            <fieldset>
                <legend className="mb-0 py-3">
                    <p className="filter-courses-subtitle font-weight-semi-bold my-0 text-black">COURSE TOPICS</p>
                </legend>
                {productCategories.map((topic) => {
                    let totalChildTopics = 0;
                    let checkedChildTopics = 0;
                    const childTopics = topic.subCategories?.map((subTopic) => {
                        const checked = subTopics.has(subTopic.seoName);
                        checkedChildTopics += Number(checked);
                        totalChildTopics++;
                        return (
                            <CourseFilterCheckbox
                                checkboxName={SearchParamNames.SubTopics}
                                checkboxValue={subTopic.seoName}
                                checked={checked}
                                className="ml-4 mb-2"
                                handleFilterChange={handleFilterChange}
                                idPrefix={idPrefix}
                                key={subTopic.name}
                                label={subTopic.name}
                            />
                        );
                    });

                    return (
                        <CourseFilterCheckboxGroup
                            checkboxName={SearchParamNames.Topics}
                            checkboxValue={topic.seoName}
                            checked={topics.has(topic.seoName) && checkedChildTopics === totalChildTopics}
                            courseCount={courseCountMap[topic.seoName]}
                            defaultExpanded={!!checkedChildTopics}
                            handleFilterChange={handleFilterChange}
                            idPrefix={idPrefix}
                            indeterminate={!!checkedChildTopics && checkedChildTopics < totalChildTopics}
                            key={topic.name}
                            label={topic.name}
                        >
                            {childTopics}
                        </CourseFilterCheckboxGroup>
                    );
                })}
            </fieldset>

            {isModal && (
                <>
                    <hr className="my-4" />
                    <Button className="d-block mx-auto my-4" onClick={onHide} size="lg">
                        Apply Filters
                    </Button>
                    {/* Extra space for live chat button */}
                    <div className="py-4" />
                </>
            )}
        </>
    );
};

export { FilterCourses, FilterCoursesProps };
